import React from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const ExportHistory = ({ exports, onRedownload, onDeleteExport }) => {
  const formatFileSize = (bytes) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i))?.toFixed(2)) + ' ' + sizes?.[i];
  };

  const formatDate = (date) => {
    return new Date(date)?.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getFormatIcon = (format) => {
    switch (format?.toLowerCase()) {
      case 'mp4':
        return 'Video';
      case 'mov':
        return 'Film';
      case 'avi':
        return 'FileVideo';
      default:
        return 'File';
    }
  };

  const isExpired = (expiresAt) => {
    return new Date(expiresAt) < new Date();
  };

  const getTimeUntilExpiry = (expiresAt) => {
    const now = new Date();
    const expiry = new Date(expiresAt);
    const diffMs = expiry - now;
    
    if (diffMs <= 0) return 'Expired';
    
    const days = Math.floor(diffMs / (1000 * 60 * 60 * 24));
    const hours = Math.floor((diffMs % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
    
    if (days > 0) return `${days}d ${hours}h`;
    if (hours > 0) return `${hours}h`;
    
    const minutes = Math.floor((diffMs % (1000 * 60 * 60)) / (1000 * 60));
    return `${minutes}m`;
  };

  return (
    <div className="bg-card border border-border rounded-lg">
      <div className="p-4 border-b border-border">
        <div className="flex items-center justify-between">
          <h3 className="text-lg font-semibold text-foreground">Export History</h3>
          <div className="flex items-center space-x-2 text-sm text-muted-foreground">
            <Icon name="Download" size={16} />
            <span>{exports.length} exports</span>
          </div>
        </div>
      </div>
      <div className="divide-y divide-border max-h-96 overflow-y-auto">
        {exports.length === 0 ? (
          <div className="p-8 text-center">
            <div className="w-12 h-12 bg-muted rounded-full flex items-center justify-center mx-auto mb-3">
              <Icon name="Download" size={24} className="text-muted-foreground" />
            </div>
            <p className="text-muted-foreground">No exports available</p>
          </div>
        ) : (
          exports.map((exportItem) => {
            const expired = isExpired(exportItem?.expiresAt);
            const timeLeft = getTimeUntilExpiry(exportItem?.expiresAt);
            
            return (
              <div key={exportItem?.id} className="p-4">
                <div className="flex items-start justify-between mb-3">
                  <div className="flex items-center space-x-3">
                    <div className="w-10 h-10 bg-primary/10 rounded-lg flex items-center justify-center">
                      <Icon name={getFormatIcon(exportItem?.format)} size={20} className="text-primary" />
                    </div>
                    <div>
                      <div className="font-medium text-foreground">{exportItem?.projectName}</div>
                      <div className="text-sm text-muted-foreground">
                        {exportItem?.format?.toUpperCase()} • {exportItem?.resolution} • {formatFileSize(exportItem?.fileSize)}
                      </div>
                    </div>
                  </div>
                  
                  <div className="flex items-center space-x-2">
                    {!expired ? (
                      <Button
                        variant="outline"
                        size="sm"
                        iconName="Download"
                        onClick={() => onRedownload(exportItem?.id)}
                      >
                        Download
                      </Button>
                    ) : (
                      <span className="text-sm text-red-600 font-medium">Expired</span>
                    )}
                    <Button
                      variant="ghost"
                      size="sm"
                      iconName="Trash2"
                      onClick={() => onDeleteExport(exportItem?.id)}
                    >
                      Delete
                    </Button>
                  </div>
                </div>
                <div className="grid grid-cols-2 lg:grid-cols-3 gap-4 text-sm">
                  <div>
                    <span className="text-muted-foreground">Exported:</span>
                    <div className="font-medium">{formatDate(exportItem?.createdAt)}</div>
                  </div>
                  <div>
                    <span className="text-muted-foreground">Downloads:</span>
                    <div className="font-medium">{exportItem?.downloadCount}</div>
                  </div>
                  <div>
                    <span className="text-muted-foreground">
                      {expired ? 'Expired:' : 'Expires in:'}
                    </span>
                    <div className={`font-medium ${expired ? 'text-red-600' : timeLeft?.includes('d') ? 'text-green-600' : 'text-yellow-600'}`}>
                      {timeLeft}
                    </div>
                  </div>
                </div>
                {/* Quality and Settings Info */}
                <div className="mt-3 flex flex-wrap gap-2">
                  <span className="px-2 py-1 bg-muted text-muted-foreground text-xs rounded-full">
                    {exportItem?.quality}
                  </span>
                  <span className="px-2 py-1 bg-muted text-muted-foreground text-xs rounded-full">
                    {exportItem?.fps} FPS
                  </span>
                  {exportItem?.hasAudio && (
                    <span className="px-2 py-1 bg-muted text-muted-foreground text-xs rounded-full">
                      Audio
                    </span>
                  )}
                  {exportItem?.hasSubtitles && (
                    <span className="px-2 py-1 bg-muted text-muted-foreground text-xs rounded-full">
                      Subtitles
                    </span>
                  )}
                </div>
              </div>
            );
          })
        )}
      </div>
    </div>
  );
};

export default ExportHistory;